/**
 * CSV export for the leads list.
 *
 * Fields are user-controlled (display names, emails), so every cell is escaped
 * for CSV quoting AND guarded against spreadsheet formula injection: a value
 * starting with = + - @ (or tab / CR) is executed as a formula by Excel and
 * Sheets when the export is opened, which turns an exported lead list into a
 * remote-content or data-exfil vector. Prefixing with a single quote neutralises
 * it while keeping the value readable.
 */

import type { Lead } from './hooks/useLeads';

const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

export const escapeCsvCell = (value: unknown): string => {
  const raw = value === null || value === undefined ? '' : String(value);
  const safe = FORMULA_TRIGGER.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
};

const isoDate = (d?: Date): string =>
  d && !isNaN(d.getTime()) && d.getTime() > 0 ? d.toISOString().split('T')[0] : '';

export const LEAD_CSV_HEADERS = [
  'Name',
  'Email',
  'Phone',
  'Category',
  'Source',
  'Status',
  'Email verified',
  'Registered',
  'Last login',
];

export const buildLeadsCsv = (leads: Lead[]): string => {
  const rows = leads.map((l) => [
    l.name,
    l.email,
    l.phone,
    l.category,
    l.source === 'legacy' ? 'Legacy import' : 'App signup',
    l.isSuspended ? 'Suspended' : 'Active',
    l.emailVerified ? 'Yes' : 'No',
    isoDate(l.createdAt),
    isoDate(l.lastLoginAt),
  ]);

  return [LEAD_CSV_HEADERS, ...rows]
    .map((r) => r.map(escapeCsvCell).join(','))
    .join('\n');
};

export const downloadLeadsCsv = (leads: Lead[], filename: string): void => {
  // BOM so Excel reads UTF-8 names (Arabic host names) correctly.
  const blob = new Blob([`\uFEFF${buildLeadsCsv(leads)}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
